import { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import AppHeader from './AppHeader';
import BottomNav from './BottomNav';
import { REALTOR_TABS } from '../../config/ieumConfig';
import ieumLogo from '../../assets/ieum-logo.png';
import pcBg from '../../assets/pc-bg.png';
import {
  subscribeLatestNoticeTs,
  getLastReadAt,
  markAllRead,
} from '../../services/notificationService';

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};

  @media (min-width: 1281px) {
    background-color: #EEF2F7;
    background-image: url(${pcBg});
    background-repeat: no-repeat;
    background-position: center bottom;
    background-size: min(1800px, 100%) auto;
    background-attachment: fixed;
  }
`;

/* PC 상단 헤더 (769px 이상) — 중앙 480 프레임 기준 grid 정렬 */
const PcHeader = styled.header`
  display: none;

  @media (min-width: 1281px) {
    display: grid;
    grid-template-columns: 1fr 480px 1fr;
    align-items: center;
    height: 88px;
    background: #fff;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    position: sticky;
    top: 0;
    z-index: 50;
  }
`;

const PcBrand = styled.div`
  justify-self: end;
  padding-right: 40px;
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 44px;
    height: 44px;
    object-fit: contain;
  }

  span {
    font-size: 22px;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.text};
    letter-spacing: -0.5px;
  }
`;

const PcTabs = styled.nav`
  grid-column: 2 / 4;
  justify-self: start;
  display: flex;
  gap: 44px;
  height: 100%;
  align-items: center;
  white-space: nowrap;
`;

const PcTab = styled(NavLink)`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 4px;
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color 0.15s;

  &:hover { color: ${({ theme }) => theme.colors.text}; }

  &.active {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 22px;
      height: 3px;
      background: ${({ theme }) => theme.colors.primary};
      border-radius: 2px;
    }
  }
`;

const PcBadgeDot = styled.span`
  position: absolute;
  top: 28px;
  right: -8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #EF4444;
`;

/* AppHeader는 모바일에서만 표시 */
const MobileOnly = styled.div`
  @media (min-width: 1281px) {
    display: none;
  }
`;


/* 중앙 모바일 프레임 */
const Frame = styled.div`
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
  position: relative;

  @media (min-width: 1281px) {
    margin: 24px auto;
    min-height: calc(100vh - 112px);
    box-shadow: 0 10px 40px rgba(74, 108, 247, 0.12);
    background: ${({ theme }) => theme.colors.bg};
  }
`;

const Content = styled.main`
  padding: ${({ theme }) => theme.realtor.pagePadding};
  padding-top: ${({ $noHeader, theme }) => $noHeader ? theme.realtor.pagePadding : `calc(52px + ${theme.realtor.pagePadding})`};
  padding-bottom: 76px;

  @media (min-width: 1281px) {
    padding-top: ${({ theme }) => theme.realtor.pagePadding};
    padding-bottom: 24px;
  }
`;

/* PC 풋바 (빈 구분선) */
const PcFooter = styled.footer`
  display: none;

  @media (min-width: 1281px) {
    display: block;
    height: 100px;
    background: #1F2937;
    border-top: 1px solid #111827;
    margin-top: auto;
  }
`;

// AppHeader가 숨겨지는 경로
const NO_HEADER_PATHS = ['/realtor', '/realtor/recommend', '/realtor/recommend/customer', '/realtor/recommend/realtor', '/realtor/recommend/qr', '/realtor/recommend/manage', '/realtor/manage/referral', '/realtor/manage/settlement'];

const RealtorLayout = () => {
  const { pathname } = useLocation();
  const noHeader = pathname === '/realtor' || pathname.startsWith('/realtor/recommend') || ['/realtor/manage/referral', '/realtor/manage/settlement'].includes(pathname);
  const [latestTs, setLatestTs] = useState(0);
  const [lastReadAt, setLastReadAt] = useState(() => getLastReadAt());
  const hasUnread = latestTs > 0 && latestTs > lastReadAt;

  // 페이지 전환 시 상단으로 스크롤 + fixed positioning / 클릭 영역 reflow 강제
  // (BottomNav, 메뉴 항목 등이 첫 렌더 직후 한 박자 안 먹는 문제 회피)
  // 폴드7 등 일부 디바이스에서 1회 만으로 안 먹는 케이스 → 다단계로 강제
  useEffect(() => {
    const tick = () => {
      try {
        window.scrollTo(0, 0);
        // eslint-disable-next-line no-unused-expressions
        document.body && document.body.offsetHeight;
        window.dispatchEvent(new Event('resize'));
      } catch (_) { /* skip */ }
    };
    const rafId = requestAnimationFrame(tick);
    const t1 = setTimeout(tick, 100);
    const t2 = setTimeout(tick, 300);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname]);

  // 최신 공지 createdAt 실시간 구독 → 새 공지 등록되면 즉시 반영
  useEffect(() => {
    const unsub = subscribeLatestNoticeTs((ts) => setLatestTs(ts));
    return () => { try { unsub && unsub(); } catch (_) { /* noop */ } };
  }, []);

  // 알림 페이지 진입 시 모두 읽음 처리
  useEffect(() => {
    if (pathname === '/realtor/notifications') {
      markAllRead();
      setLastReadAt(Date.now());
    }
  }, [pathname]);

  return (
    <Page>
      <PcHeader>
        <PcBrand>
          <img src={ieumLogo} alt="이음" />
          <span>이음</span>
        </PcBrand>
        <PcTabs>
          {REALTOR_TABS.map((tab) => (
            <PcTab key={tab.key} to={tab.path} end={tab.path === '/realtor'}>
              {tab.label}
            </PcTab>
          ))}
          <PcTab to="/realtor/notifications">
            알림
            {hasUnread && <PcBadgeDot />}
          </PcTab>
          <PcTab to="/realtor/inquiry">문의</PcTab>
        </PcTabs>
      </PcHeader>

      <Frame>
        <MobileOnly>
          <AppHeader />
        </MobileOnly>
        <Content $noHeader={noHeader}>
          <Outlet />
        </Content>
        <BottomNav tabs={REALTOR_TABS} />
      </Frame>

      <PcFooter />
    </Page>
  );
};

export default RealtorLayout;
